// src/v2/hooks/useCountUp.js
//
// Count-up microinteraction hook for KPI tile strips.
// Animates a numeric value from ~90% of `target` in ~400 ms with an ease-out
// curve and a per-tile cascade stagger of `index * 30 ms`.
//
// Re-fires only when `animationKey` changes so filter changes (which update
// `target` but not `animationKey`) are reflected instantly with no replay.
// If `target` changes mid-animation (filter applied during the ~400 ms window),
// the animation cancels immediately and the tile snaps to the new live value.
//
// Respects `prefers-reduced-motion: reduce` — returns `target` immediately
// when the system accessibility setting is enabled or matchMedia is unavailable.
//
// Extended options (third argument):
//   Pass an options object instead of a bare number to unlock:
//     { from = "near-target", duration = 400, index = 0, disabled = false }
//   • from: "near-target" (default, 90% of target) or 0 (start from zero).
//   • duration: animation length in ms (default 400).
//   • index: stagger index (same as the legacy positional arg).
//   The legacy positional signature useCountUp(target, key, index) still works.

import { useEffect, useRef, useState } from "react";

const DEFAULT_DURATION = 400;
const STAGGER  = 30;

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion() {
  // No matchMedia → skip animation (JSDOM / SSR / legacy browsers).
  // In real browsers matchMedia is always present; this only short-circuits
  // environments where rAF frames never advance (e.g. Vitest + JSDOM).
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Count-up microinteraction hook.
 *
 * @param {number} target       Final numeric value to animate toward.
 * @param {*}      animationKey Animation reruns only when this value changes.
 * @param {number|{from?: "near-target"|0, duration?: number, index?: number, disabled?: boolean}} [options=0]
 *   Either a bare tile index (legacy) or an options object.
 * @returns {number} Live display value (0 → target while animating, `target` otherwise).
 */
export function useCountUp(target, animationKey, options = 0) {
  // Normalise the third argument — supports the legacy (target, key, index)
  // positional signature as well as the new options-object form.
  const isOptionsObj = options !== null && typeof options === "object";
  const index    = isOptionsObj ? (options.index    ?? 0)                  : options;
  const duration = isOptionsObj ? (options.duration ?? DEFAULT_DURATION)   : DEFAULT_DURATION;
  const fromZero = isOptionsObj ? (options.from === 0)                     : false;
  const disabled = isOptionsObj ? (options.disabled ?? false)              : false;

  // null  → not animating; any number → actively animating
  const [animDisplay, setAnimDisplay] = useState(null);
  const rafRef    = useRef(null);
  const timerRef  = useRef(null);
  const targetRef = useRef(target);
  const historyRef = useRef(null);

  // Always keep targetRef in sync with the latest target so the step function
  // can detect mid-animation filter changes without re-running the effect.
  targetRef.current = target;

  useEffect(() => {
    const previous = historyRef.current;
    historyRef.current = { animationKey, disabled };
    // Cancel any in-progress animation.
    if (timerRef.current != null) clearTimeout(timerRef.current);
    if (rafRef.current   != null) cancelAnimationFrame(rafRef.current);

    // Compact presentation consumes keys without starting timers/frames.
    // Re-enabling the same key must not replay a hidden or cancelled entrance.
    if (disabled || (previous?.disabled && Object.is(previous.animationKey, animationKey)) || prefersReducedMotion()) {
      setAnimDisplay(null);
      return;
    }

    // Zero target — no animation needed.
    if (Math.abs(target) === 0) {
      setAnimDisplay(null);
      return;
    }

    const computeStart = (t) => fromZero ? 0 : Math.round(t * 0.9);
    const startValue = computeStart(target);
    setAnimDisplay(startValue);

    timerRef.current = setTimeout(() => {
      let startTs = null;
      const capturedTarget = targetRef.current; // snapshot at animation-start time
      const capturedStart  = computeStart(capturedTarget);

      function step(ts) {
        if (startTs == null) startTs = ts;
        // If target changed mid-animation (e.g. filter applied), snap and stop
        // so the tile immediately reflects the new filtered value.
        if (targetRef.current !== capturedTarget) {
          setAnimDisplay(null);
          return;
        }
        const t   = Math.min((ts - startTs) / duration, 1);
        const val = Math.round(capturedStart + easeOut(t) * (capturedTarget - capturedStart));
        if (t < 1) {
          setAnimDisplay(val);
          rafRef.current = requestAnimationFrame(step);
        } else {
          // Animation complete — fall back to live `target` for instant filter updates.
          setAnimDisplay(null);
        }
      }

      rafRef.current = requestAnimationFrame(step);
    }, index * STAGGER);

    return () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
      if (rafRef.current   != null) cancelAnimationFrame(rafRef.current);
      setAnimDisplay(null);
    };
  }, [animationKey, disabled]); // intentional: target/options changes alone do not replay

  // While animating return the in-progress value; otherwise follow `target` live.
  return !disabled && animDisplay !== null ? animDisplay : target;
}
